import { useEffect, useState } from 'react';
import type { AgentApiJobPostImportRequestsRecord } from '@/types/serializers';
import Select from '@/components/Select';
import Checkbox from '@/components/Checkbox';
import { Button } from '@/components/Button';
import useUpdateJobPostImportRequest from '../../api/useUpdateJobPostImportRequest';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import { LiaArrowRightSolid } from 'react-icons/lia';

type Props = {
  importRequest: AgentApiJobPostImportRequestsRecord;
};

type ColType = AgentApiJobPostImportRequestsRecord['cols_mapping'][number]['col_type'];

export function JobPostImportRequestsMappingTemplate({ importRequest }: Props) {
  const colsMapping = importRequest.cols_mapping;
  // 利用するheaderのindex配列を保持（Setで管理）
  const [enabledIndexes, setEnabledIndexes] = useState<Set<number>>(
    new Set(importRequest.cols_mapping.map((col) => col.index))
  );
  // col_typeのマッピングを保持（Mapで管理）
  const [colTypeMap, setColTypeMap] = useState<Map<number, ColType>>(
    new Map(importRequest.cols_mapping.map((col) => [col.index, col.col_type]))
  );
  const { mutateAsync: updateJobPostImportRequest, isPending } = useUpdateJobPostImportRequest(
    importRequest.encrypt_id
  );

  useEffect(() => {
    setEnabledIndexes(new Set(importRequest.cols_mapping.map((col) => col.index)));
    setColTypeMap(new Map(importRequest.cols_mapping.map((col) => [col.index, col.col_type])));
  }, [importRequest]);

  const handleSave = async () => {
    try {
      // enabledIndexesに含まれるindexのみを含むcolsMappingを構築
      const colsMappingToSend = colsMapping
        .filter((col) => enabledIndexes.has(col.index))
        .map((col) => {
          const col_type = colTypeMap.get(col.index);
          if (!col_type) {
            throw new Error(`col_type not found for index ${col.index}`);
          }
          return {
            ...col,
            col_type,
          };
        });

      // ここで「企業名」「求人タイトル」が1つずつ含まれているかチェック
      const titleCount = colsMappingToSend.filter((col) => col.col_type === 'title').length;
      const companyCount = colsMappingToSend.filter((col) => col.col_type === 'company').length;

      if (titleCount !== 1) {
        showErrorToast('求人タイトルは1つ選択してください');
        return;
      }

      if (companyCount !== 1) {
        showErrorToast('企業名は1つ選択してください');
        return;
      }

      await updateJobPostImportRequest({ cols_mapping: colsMappingToSend });
      showSuccessToast('取り込みを開始しました');
    } catch (error) {
      console.error(error);
      showErrorToast('取り込みに失敗しました。もう一度お試しください。');
    }
  };

  return (
    <>
      <div className="flex flex-col gap-md pb-[96px]">
        <div className="flex flex-col gap-xs">
          <h1 className="text-xl font-bold text-main">CSV項目の紐付け</h1>
          <p className="text-sm text-main">※ 求人タイトルと企業名は必ず選択してください。</p>
        </div>

        <div className="flex flex-col">
          {/* ヘッダー */}
          <div className="flex h-xxl items-center gap-md bg-gray_darken px-3">
            <div className="w-x4l text-base font-bold text-main">取り込み</div>
            <div className="w-[451px] text-base font-bold text-main">CSV項目名</div>
            <div className="flex-1 text-base font-bold text-main">紐づけ先</div>
          </div>

          {/* アイテム */}
          {colsMapping.map(({ index, header, sample }) => {
            const col_type = colTypeMap.get(index);
            const isEnabled = enabledIndexes.has(index);
            return (
              <div
                key={index}
                className="hidden-scrollbar flex h-[68px] items-center gap-md overflow-x-auto border-b border-solid border-border bg-white px-3 hover:bg-gray-50"
              >
                <div className="flex min-w-x4l justify-center">
                  <Checkbox
                    checked={isEnabled}
                    onChange={(checked) => {
                      setEnabledIndexes((prev) => {
                        const next = new Set(prev);
                        if (checked) {
                          next.add(index);
                          setColTypeMap((prevMap) => {
                            if (!prevMap.has(index)) {
                              const nextMap = new Map(prevMap);
                              nextMap.set(index, 'job_content');
                              return nextMap;
                            }
                            return prevMap;
                          });
                        } else {
                          next.delete(index);
                        }
                        return next;
                      });
                    }}
                  />
                </div>
                <div className="flex max-w-[411px] min-w-[411px] flex-col gap-1">
                  <span className="text-base font-bold text-main">{header || '-'}</span>
                  <span className="truncate text-sm text-sub">{sample}</span>
                </div>
                <div className="min-w-lg">
                  <LiaArrowRightSolid size={24} className="text-sub" />
                </div>
                <div className="flex-1">
                  <div className="w-[316px]">
                    <Select
                      className="w-full"
                      value={col_type || ''}
                      disabled={!isEnabled}
                      onValueChange={(value) => {
                        if (!value) return;
                        const nextColType = value as ColType;
                        setColTypeMap((prev) => {
                          const next = new Map(prev);
                          next.set(index, nextColType);
                          return next;
                        });
                      }}
                      items={[
                        { value: 'title', label: '求人タイトル' },
                        { value: 'company', label: '企業名' },
                        { value: 'job_content', label: '求人項目に含める' },
                        { value: 'agent_content', label: '社内メモに含める' },
                      ]}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-10 flex w-full justify-end gap-2 bg-white px-sm py-[14px] shadow-layer3">
        <Button type="button" onClick={handleSave} disabled={isPending} color="primary" size="lg">
          {isPending ? '取り込み中...' : '取り込みを開始'}
        </Button>
      </div>
    </>
  );
}
